import React, { useCallback, useState } from 'react';

import { Button, PopUp, Dialog, DialogHeader, DialogContent } from '@ergeon/core-components';

import { type ModalHandler } from '../index';
import { useModalClose } from '../../hooks/useModalClose';

const TITLE = 'Return Activity to Queue';
const CONFIRMATION_MESSAGE = 'Are you sure you want to return this activity to the queue?';
const HELPER_TEXT = 'The activity will be unassigned and available for other team members.';
const BUTTON_TEXT = 'Return to Queue';
const BUTTON_LOADING_TEXT = 'Returning...';

type Props = {
  modal: ModalHandler;
  activityTitle: string;
  onConfirm: () => Promise<boolean>;
};

const ReturnToQueueModal: React.FC<Props> = ({ modal, activityTitle, onConfirm }) => {
  const [isLoading, setIsLoading] = useState(false);
  const closeModal = useModalClose(modal);

  const handleConfirm = useCallback(async() => {
    setIsLoading(true);
    try {
      const success = await onConfirm();
      if (success) {
        closeModal();
      }
    } catch (error) {
      console.error('Failed to return activity to queue:', error);
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, closeModal]);

  return (
    <PopUp isVisible={modal.visible} maxWidth="small" onChangeVisible={closeModal}>
      <Dialog isVisible={modal.visible}>
        <DialogHeader onDismiss={closeModal} title={TITLE} />
        <DialogContent>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700 dark:text-gray-300 paragraph-2">{CONFIRMATION_MESSAGE}</p>
            <p className="font-medium text-gray-900 dark:text-white paragraph-1">"{activityTitle}"</p>
            <p className="text-gray-500 dark:text-gray-400 paragraph-3">* {HELPER_TEXT}</p>
            <div className="flex gap-2">
              <Button
                flavor="regular" isFullWidth onClick={closeModal} taste="line"
                type="button">
                Cancel
              </Button>
              <Button
                disabled={isLoading} flavor="primary" isFullWidth onClick={handleConfirm}
                type="button">
                {isLoading ? BUTTON_LOADING_TEXT : BUTTON_TEXT}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PopUp>
  );
};

export default ReturnToQueueModal;
